import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryStates } from 'nuqs';
import { useNavigate } from 'react-router-dom';
import { Card } from '@clay-run/glaze/card';
import { Text } from '@clay-run/glaze/text';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { ArrowLeft } from '@phosphor-icons/react/dist/icons/ArrowLeft';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { LEAD_SCANNER_CAMPAIGNS_QUERY_KEY, mapLauncherCampaignsResponse, type Campaign } from '../campaigns';
import { useAppBasePath } from '../../_shared/base-path';
import { useAllScansQuery, useScanApi } from '../hooks/useScanApi';
import { useIsWideViewport } from '../hooks/useIsWideViewport';
import { intakeBatchProgress, useLeadScannerStore } from '../store';
import { linkedScanFilterClears, scansPageParams, type ScansFilters } from '../scans-url-state';
import ScanListCard from './ScanListCard';
import ActiveScanPanel from './ActiveScanPanel';

/**
 * The unified Scans page (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md) — one filterable,
 * URL-shareable list replacing both Home's embedded draft/recent-scans lists
 * and the separate admin-only `AllSubmissions.tsx` (deleted). Defaults to
 * "my scans only" (`?mine=` default, invisible in the URL) but any
 * authenticated rep can clear it to see every rep's scans across every
 * campaign — a deliberate reversal of the round-2 plan's admin-only gate on
 * cross-rep visibility (see `api.ts`'s file-level comment).
 *
 * One data fetch (`useAllScansQuery` — every rep, every campaign, unfiltered
 * server-side); every filter (submitter/status/campaign) and the
 * currently-open scan are applied CLIENT-SIDE via the pure helpers in
 * `scans-url-state.ts`, and all of it lives in the URL so a rep can text a
 * teammate a link that opens one specific scan directly — "an explicit link
 * always wins" over whatever filter would otherwise hide it (the effect
 * below). The list card itself (search/filters/count/rows) is
 * `ScanListCard.tsx` (QA-fix batch, Fix 1) — shared with `Home.tsx`'s
 * "Scans" card rather than reimplemented here.
 */
export default function ScansPage() {
  const navigate = useNavigate();
  const appBase = useAppBasePath();
  const isWide = useIsWideViewport();
  const { apiFetch } = useApi();
  const { user } = useCurrentUser();
  const me = user?.ownerEmail ?? user?.email ?? null;

  const [params, setParams] = useQueryStates(scansPageParams);
  const intakeBatchIds = useLeadScannerStore((s) => s.intakeBatchIds);

  const scansQuery = useAllScansQuery(me);
  const scans = scansQuery.data ?? [];

  const deleteScan = useScanApi().deleteScan;

  const campaignsQuery = useQuery({
    queryKey: LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiFetch('/api/tools/launcher/campaigns');
      const json = await res.json();
      return mapLauncherCampaignsResponse(json);
    },
  });
  const campaigns = campaignsQuery.data ?? [];
  const campaignNames = new Map(campaigns.map((c: Campaign) => [c.id, c.name]));

  const filters: ScansFilters = {
    mine: params.mine,
    rep: params.rep,
    status: params.status,
    campaign: params.campaign,
    q: params.q,
  };

  // ─── "An explicit link always wins" — but only ONCE per scan-open event ──
  // If `?scan=<id>` resolves to a real scan the CURRENT filters would hide,
  // relax exactly the filters that hide it. This used to re-derive and
  // re-apply on EVERY render with no guard distinguishing "a scan was just
  // opened" from "the rep changed a filter while a scan happens to be open"
  // — with a scan open, picking a status/campaign the open scan doesn't
  // match got instantly reverted on the very next render, so the filter
  // appeared to do nothing (live-QA round-3 bug 4). `relaxedForScanRef`
  // records which scan id has already had its one-time relaxation: it
  // resets the moment `params.scan` goes falsy (closing or switching away
  // from a scan), which also satisfies the StrictMode-resilient-effects rule
  // that a lifetime ref must reset at the TOP of the mount effect, not only
  // in cleanup — this branch re-evaluates fresh every time `params.scan`
  // becomes falsy, not just once at mount. A StrictMode/remount re-run that
  // re-relaxes the same scan id is harmless: the relaxation is idempotent
  // once the filters no longer hide it. Also waits for campaigns to load
  // (not just scans): `linkedScanFilterClears`'s `q`-clearing check needs
  // `campaignNames` to correctly decide whether to clear the search term, so
  // running before campaigns land could clear `q` when it didn't need to.
  const relaxedForScanRef = useRef<string | null>(null);
  useEffect(() => {
    if (!params.scan) {
      relaxedForScanRef.current = null;
      return;
    }
    if (relaxedForScanRef.current === params.scan) return;
    if (me === null || scansQuery.isLoading || campaignsQuery.isLoading) return;
    const linked = scans.find((s) => s.id === params.scan);
    if (!linked) return;
    relaxedForScanRef.current = params.scan;
    const clears = linkedScanFilterClears(linked, filters, me, campaignNames);
    if (clears) void setParams(clears);
  });

  const activeScan = params.scan ? (scans.find((s) => s.id === params.scan) ?? null) : null;
  const activeScanNotFound = params.scan !== null && !scansQuery.isLoading && activeScan === null;

  const batchProgress = intakeBatchProgress(intakeBatchIds, scans);

  function openScan(id: string) {
    void setParams({ scan: id });
  }

  function closeScan() {
    void setParams({ scan: null });
  }

  const listCard = (
    <ScanListCard
      scans={scans}
      isLoading={scansQuery.isLoading}
      me={me}
      filters={filters}
      onFiltersChange={(patch) => setParams(patch)}
      campaigns={campaigns}
      showSubmitterFilter
      showCampaignFilter
      onOpenScan={openScan}
      onDeleteScan={(id) => deleteScan(id)}
      batchProgress={batchProgress}
      isWide={isWide}
      selectedScanId={params.scan}
    />
  );

  const detailPane = activeScanNotFound ? (
    <Card padded>
      <Text size="sm" variant="tertiary">
        Scan not found.
      </Text>
    </Card>
  ) : activeScan ? (
    <ActiveScanPanel scan={activeScan} />
  ) : params.scan ? (
    <Text size="sm" variant="tertiary">
      Loading…
    </Text>
  ) : (
    <Text size="sm" variant="tertiary">
      Select a scan to review.
    </Text>
  );

  const header = (
    <div className="flex items-center gap-2">
      <IconButton
        icon={ArrowLeft}
        aria-label="Back to scanning"
        variant="ghost"
        onClick={() => navigate(appBase)}
      />
      <Text size="sm" weight="semibold">
        Scans
      </Text>
    </div>
  );

  if (isWide) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-[minmax(320px,420px)_minmax(0,1fr)] gap-4">
          {listCard}
          <div>{detailPane}</div>
        </div>
      </div>
    );
  }

  if (params.scan) {
    return (
      <div className="flex flex-col gap-4">
        <IconButton icon={ArrowLeft} aria-label="Back to Scans" variant="ghost" className="self-start" onClick={closeScan} />
        {detailPane}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      {listCard}
    </div>
  );
}
